const WEBRTC_BASE_URL = 'https://media.n-gps.com'
// Where the JT1078 media server's /status endpoint is reachable. In
// production it rides on the media host (edge routes /media-status/ ->
// jt1078:10004); for local dev, point VUE_APP_MEDIA_STATUS_URL straight
// at the box, e.g. http://media-ip.n-gps.com:10004.
const MEDIA_STATUS_BASE = process.env.VUE_APP_MEDIA_STATUS_URL || `${WEBRTC_BASE_URL}/media-status`
// Event-attachment sink (upload service, edge-proxied at /attachments/). Holds
// the clip + snapshots a camera POSTs after a VIDEOUPLOAD, keyed by IMEI and the
// 32-hex alarm identifier.
const MEDIA_ATTACHMENTS_BASE = process.env.VUE_APP_MEDIA_ATTACHMENTS_URL || `${WEBRTC_BASE_URL}/attachments`
const JT1078_SERVER = 'media-ip.n-gps.com'
const JT1078_PORT = 10002
const JT1078_PLAYBACK_PORT = 10003
const sendCommand = (command) => fetch('/api/commands/send', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(command)
}).then(response => {
  if (!response.ok) {
    throw new Error(`Command failed: ${response.status}`)
  }
})

const wakeCameraBySms = (deviceId) => fetch(`/pinmeapi/devices/${deviceId}/camera/wake`, {
  method: 'POST',
  credentials: 'include'
}).then(response => {
  if (!response.ok) {
    throw new Error(`Camera wake-up failed: ${response.status}`)
  }
})

export const startStreaming = async(device, channel = 1) => {
  const jimiCamera = [55, 56, 57].includes(Number(device.attributes && device.attributes.deviceType))
  if (device.status === 'offline' && jimiCamera) {
    await wakeCameraBySms(device.id)
  }
  await sendCommand({
    deviceId: device.id,
    type: 'videoStart',
    textChannel: false,
    attributes: {
      server: JT1078_SERVER,
      port: JT1078_PORT,
      index: channel
    }
  })
}

export const queryRecordings = async(deviceId, channel, startTime, endTime, alarmFlag = 0) => {
  await sendCommand({
    deviceId,
    type: 'videoList',
    textChannel: false,
    attributes: {
      index: channel,
      startTime,
      endTime,
      // JT/T 1078 0x9205 alarm bitmask; 0 lets the camera return every recording.
      alarmFlag
    }
  })
}

// Ask the camera for its terminal parameters. With no `ids`, sends JT/T 808
// 0x8104 (query all); with an array of numeric ids, sends 0x8106 (query
// specific) so extended blocks that a device hides from "query all" can still
// be probed. Either way the reply is 0x0104 -> the "parameters" attribute
// (a JSON object of "0x<id>" -> hex value).
export const queryDeviceParameters = async(deviceId, ids) => {
  const command = {
    deviceId,
    type: 'getDeviceStatus',
    textChannel: false
  }
  if (Array.isArray(ids) && ids.length) {
    command.attributes = { data: ids.map(id => id.toString(16)).join(',') }
  }
  await sendCommand(command)
}

// Parameter ids probed explicitly via 0x8106 - the active-safety blocks a
// device may not include in a "query all" response.
export const SAFETY_PARAMETER_IDS = [0x64, 0x65, 0xF364, 0xF365, 0xF366, 0xF367]

// Ask the camera for its model / hardware / firmware version (JT/T 808 0x8107).
// Traccar decodes the 0x0107 reply into terminalModel / versionHw / versionFw
// position attributes.
export const queryDeviceVersion = async(deviceId) => {
  await sendCommand({
    deviceId,
    type: 'getVersion',
    textChannel: false
  })
}

// The Jimi cameras expose no usable JT/T 808 parameter block for active safety -
// 0x8104 / 0x8106 come back empty for every ADAS/DSM id. The readback is the Jimi
// text protocol: sending "NAME#" with no value makes the camera reply with its
// current setting as a 0x0900/0xF0 transparent string, which Traccar surfaces as
// the position "result" attribute. Which commands have a query form is per model
// and was found by probing the live fleet:
//   57 JC450 - only DMSSEP# answers (the DMS event on/off map). DMSSP#/DMSSEN#/
//              ADASSEN#/... reply "command error" or "not matched".
//   56 JC371 - CHECKVIDEO# returns servidor + DMS/ADAS + camera config in one go.
//   55 JC181 - no camera AI; its "Eventos" group is the equivalent safety config
//              (overspeed, collision, harsh accel/brake, harsh turn, fatigue,
//              vibration) and every one answers a bare-name query.
// Per model (deviceType). JC371 has a real "Consultas" group in the Jimi wiki;
// JC450 answers a bare-name query for its IA/ADAS/DMS settings; JC181 answers
// its "Eventos" commands and VERSION#.
export const SAFETY_TEXT_QUERIES = {
  57: ['VERSION#', 'DMSSEP#', 'DMSSP#', 'DMSVI#', 'DMSPI#', 'DMSVSP#'],
  56: ['STATUS#', 'STATUSVIDEO#', 'CHECK#', 'CHECKVIDEO#', 'VERSION#', 'PARAM#'],
  55: ['SPEED#', 'COLLIDE#', 'SPEEDCHECK#', 'SWERVE#', 'FATIGUE#', 'SENALM#', 'VERSION#']
}

// Send a batch of raw Jimi text commands (JT/T 808 0x8900 / 0xF0). Sequential so
// the camera answers one 0x0900 at a time instead of a burst. `commands` are the
// literal strings, e.g. "DMSSEP#".
export const queryTextCommands = async(deviceId, commands) => {
  for (const data of commands) {
    await sendCommand({ deviceId, type: 'custom', textChannel: false, attributes: { data } })
  }
}

const JC181_EVENT_NAMES = {
  SPEED: 'Excesso de velocidade',
  COLLIDE: 'Colisão',
  SPEEDCHECK: 'Aceleração / frenagem brusca',
  SWERVE: 'Curva brusca',
  FATIGUE: 'Fadiga',
  SENALM: 'Vibração'
}

const DMS_EVENT_NAMES = {
  fcw: 'Colisão frontal',
  hmw: 'Distância de seguimento',
  ldw: 'Saída de faixa',
  pcw: 'Colisão c/ pedestre',
  dfw: 'Distração',
  yawn: 'Bocejo',
  fsw: 'Sem rosto',
  call: 'Uso de celular',
  smoke: 'Fumando',
  ycw: 'Cinto de segurança',
  hid: 'Rosto encoberto',
  glass: 'Óculos escuros'
}

// DMSSEP,<tipo_evento>,<0|1># codes (Jimi wiki, matching DMSVI's numbering).
// pcw/hid/glass have no DMSSEP code, so they can't be toggled.
const DMS_EVENT_CODES = {
  fcw: 1, hmw: 2, ldw: 3, dfw: 5, yawn: 6, fsw: 7, call: 8, smoke: 9, ycw: 10
}

// Enable (on=true) or disable a DMS event on a JC450.
export const setDmsEvent = async(deviceId, code, on) => {
  await sendCommand({
    deviceId,
    type: 'custom',
    textChannel: false,
    attributes: { data: `DMSSEP,${code},${on ? 1 : 0}#` }
  })
}

// JC181 "Eventos" set-command layouts (Jimi wiki). Field 0 right after the name
// is the ON/OFF toggle for every event except SENALM, where field 0 is the
// vibration sensitivity: 0 disables, 1-5 enables (2 is the factory default).
const JC181_EVENT_TOGGLE = {
  SPEED: { on: 'ON', off: 'OFF' },
  COLLIDE: { on: 'ON', off: 'OFF' },
  SPEEDCHECK: { on: 'ON', off: 'OFF' },
  SWERVE: { on: 'ON', off: 'OFF' },
  FATIGUE: { on: 'ON', off: 'OFF' },
  SENALM: { on: '2', off: '0' }
}

// Enable/disable a JC181 driving event by resending its command with field 0
// flipped. `fields` is the CSV the camera echoed in its "NAME:..." query reply
// (state in field 0); every other field is kept as reported.
export const setJc181Event = async(deviceId, name, fields, on) => {
  const key = String(name || '').toUpperCase()
  const toggle = JC181_EVENT_TOGGLE[key]
  if (!toggle) return
  const parts = String(fields || '').split(',').filter(Boolean)
  if (!parts.length) return
  parts[0] = on ? toggle.on : toggle.off
  await sendCommand({
    deviceId,
    type: 'custom',
    textChannel: false,
    attributes: { data: `${key},${parts.join(',')}#` }
  })
}

// Turn a raw Jimi text-command reply (Traccar `result` attribute) into a
// renderable row. Shapes seen on the fleet:
//   "fcw:1,hmw:1,pcw:0,..."       JC450 DMSSEP# - DMS event on/off map
//   "SPEED:OFF,0,50,20"           JC181 event query - NAME + CSV, field 0 = state
//   "VERSION:..;MCUVER:..;.."      JC371 CHECK/CHECKVIDEO - semicolon k:v dump
export const describeTextResult = (text) => {
  const raw = String(text || '').trim().replace(/#+$/, '')
  if (!raw) return null

  // JC450 DMSSEP map: comma-separated key:0|1 pairs, no NAME prefix.
  if (/^[a-z]{2,6}:[01](,[a-z]{2,6}:[01])+$/i.test(raw)) {
    const events = raw.split(',').map(pair => {
      const [key, value] = pair.split(':')
      return {
        key,
        label: DMS_EVENT_NAMES[key] || key.toUpperCase(),
        on: value === '1',
        code: DMS_EVENT_CODES[key] || null
      }
    })
    return { kind: 'events', label: 'Eventos DMS', events }
  }

  // JC450 DMSSP: "adas:60;dms:30" - activation speed thresholds.
  const speeds = raw.match(/^adas:(\d+);dms:(\d+)$/i)
  if (speeds) {
    return {
      kind: 'state',
      label: 'Velocidade de ativação',
      state: `ADAS ${speeds[1]} · DMS ${speeds[2]} km/h`,
      level: 'info'
    }
  }

  // JC450 DMSVI / DMSPI: "voice Interval:fcw:30_hmw:10_ldw:5_..." - per-event seconds.
  const intervals = raw.match(/Interval:\s*(.+)$/i)
  if (intervals) {
    const pairs = intervals[1].split('_').filter(Boolean).map(part => {
      const [key, value] = part.split(':')
      return `${DMS_EVENT_NAMES[key] || (key || '').toUpperCase()}: ${value}s`
    })
    return { kind: 'pairs', label: 'Intervalo por evento', pairs }
  }

  // JC181/JC371 VERSION#: "[VERSION]xxx[MCU]yyy[MODEM]zzz" (JC181 comma-joins).
  if (/^\[[A-Za-z0-9]+\]/.test(raw)) {
    const pairs = []
    const tag = /\[([^\]]+)\]([^[]*)/g
    let match
    while ((match = tag.exec(raw)) !== null) {
      const value = match[2].replace(/,\s*$/, '').trim()
      if (value) pairs.push(`${match[1]}: ${value}`)
    }
    if (pairs.length) return { kind: 'pairs', label: 'Versão dos módulos', pairs }
  }

  // JC450 DMSVSP: "DMSVSP:0" - virtual (bench-test) speed.
  const virtual = raw.match(/^DMSVSP:(\d+)$/i)
  if (virtual) {
    return {
      kind: 'state',
      label: 'Velocidade virtual',
      state: virtual[1] === '0' ? 'desligada' : `${virtual[1]} km/h`,
      level: virtual[1] === '0' ? 'info' : 'warning'
    }
  }

  const named = raw.match(/^([A-Za-z][A-Za-z0-9]*)[:,]([\s\S]*)$/)
  const name = named ? named[1].toUpperCase() : ''
  const rest = named ? named[2].trim() : raw

  // JC371 CHECK / CHECKVIDEO: "key:value;key:value;..."
  if (rest.indexOf(';') !== -1) {
    const pairs = rest.split(';').map(part => part.trim()).filter(Boolean)
    return { kind: 'pairs', label: name || 'Configuração', pairs }
  }

  // JC181 driving event: NAME + CSV, first field is the state (ON/OFF, or 0 = off).
  if (JC181_EVENT_NAMES[name]) {
    const head = rest.split(',')[0]
    const on = /^on$/i.test(head) || (/^\d+$/.test(head) && Number(head) > 0)
    const off = /^off$/i.test(head) || head === '0'
    return {
      kind: 'state',
      label: JC181_EVENT_NAMES[name],
      state: on ? 'ligado' : off ? 'desligado' : head,
      level: on ? 'success' : off ? 'warning' : 'info',
      detail: rest,
      // Fields for the enable/disable toggle: the command name and the CSV to
      // resend with field 0 flipped.
      jc181: JC181_EVENT_TOGGLE[name] ? name : null,
      fields: rest,
      on
    }
  }

  return { kind: 'text', label: name || 'Resposta', detail: rest }
}

const hexToInt = (hex) => (hex ? parseInt(hex, 16) : 0)
const isAllZero = (hex) => !hex || /^0+$/.test(hex)

// Interpret the raw "parameters" attribute (0x0104 dump) into readable rows plus
// an active-safety summary. Values above 2^53 aren't a concern here - every
// parameter is a short fixed-width field.
export const describeParameters = (value) => {
  let raw = value
  let parseFailed = false
  if (typeof raw === 'string') {
    let json = raw
    const decoder = document.createElement('textarea')
    for (let i = 0; i < 3; i++) {
      decoder.innerHTML = json
      if (decoder.value === json) break
      json = decoder.value
    }
    try {
      raw = JSON.parse(json)
    } catch (error) {
      parseFailed = true
      raw = {}
    }
  }
  raw = raw || {}
  // A 0x8106 reply for a parameter the device does not have echoes the id with
  // an empty value, so treat '' the same as absent.
  const get = (id) => (raw[id] != null && raw[id] !== '') ? String(raw[id]) : undefined
  const rows = []
  const push = (label, val, level = 'info') => rows.push({ label, value: val, level })

  const heartbeat = get('0x1')
  if (heartbeat !== undefined) push('Intervalo de heartbeat (s)', String(hexToInt(heartbeat)), 'info')
  const maxSpeed = get('0x55')
  if (maxSpeed !== undefined) push('Velocidade máxima (km/h)', String(hexToInt(maxSpeed)), 'info')

  // Active-safety configuration. 0x64/0x65 are the JT/T 808 ADAS/DSM parameter
  // slots; 0xF364/0xF365/0xF367 are the extended T/JSATL12 (Jiangsu) blocks.
  // Absent = the firmware never exposed them; all-zero = present but disabled.
  const adas = get('0x64')
  const dms = get('0x65')
  const suAdas = get('0xf364')
  const suDms = get('0xf365')
  const blindSpot = get('0xf367')
  const alarmMask = get('0x79')

  const safetyKeys = [adas, dms, suAdas, suDms]
  const anyPresent = safetyKeys.some(v => v !== undefined)
  const anyConfigured = safetyKeys.some(v => v !== undefined && !isAllZero(v))
  let activeSafety
  if (!anyPresent) {
    activeSafety = 'not-supported'
  } else if (anyConfigured) {
    activeSafety = 'configured'
  } else {
    activeSafety = 'present-but-disabled'
  }

  // `paramId` + `current` are attached only where enableParameterValue() knows a
  // safe single-field edit; the UI shows an "enable" button for those rows.
  // Absent blocks are the norm (JC450 has no such parameters at all) - the
  // summary line already says so, so don't list a row per missing id.
  const safetyRow = (label, paramId, val) => {
    if (val === undefined) return
    const zero = isAllZero(val)
    rows.push({
      label,
      value: zero ? 'presente, não configurado' : 'configurado',
      level: zero ? 'warning' : 'success',
      paramId,
      current: val
    })
  }
  safetyRow('ADAS estendido (0xF364)', 0xF364, suAdas)
  safetyRow('DSM estendido (0xF365)', 0xF365, suDms)
  safetyRow('ADAS (0x64)', 0x64, adas)
  safetyRow('DSM (0x65)', 0x65, dms)
  safetyRow('Ponto cego (0xF367)', 0xF367, blindSpot)
  if (alarmMask !== undefined) {
    // 0x79 is the JT/T 1078 A/V alarm mask word. Polarity (mask-out vs enable)
    // varies by vendor doc, so this row is informational only - no button.
    const off = isAllZero(alarmMask)
    rows.push({
      label: 'Máscara de alarme A/V (0x79)',
      value: off ? 'sem máscara (000000)' : alarmMask,
      level: 'info'
    })
  }

  return { rows, activeSafety: parseFailed ? 'unknown' : activeSafety, raw }
}

// Given a parameter id and its current hex value (from the 0x0104 dump), return
// the hex value that enables it - or null when there is no safe single-field
// edit, the parameter is absent, or it already looks enabled. Only handles the
// T/JSATL12 ADAS/DSM blocks, where AlarmEnable is a documented enable bitmask;
// every other byte the camera reported is kept untouched.
export const enableParameterValue = (paramId, currentHex) => {
  const hex = String(currentHex || '').toLowerCase()
  if (!hex || !/^[0-9a-f]+$/.test(hex) || hex.length % 2) return null
  if (paramId === 0xF364 || paramId === 0xF365) {
    // T/JSATL12 block: AlarmEnable is bytes 12..15 of the value. Set the
    // standard-alarm bits (0x0000FFFF), leave thresholds/capture fields as-is.
    if (hex.length < 40) return null
    const alarmEnable = hex.slice(24, 32)
    if (/[^0]/.test(alarmEnable)) return null
    return hex.slice(0, 24) + '0000ffff' + hex.slice(32)
  }
  return null
}

export const setDeviceParameter = async(deviceId, parameterId, hexValue) => {
  await sendCommand({
    deviceId,
    type: 'configuration',
    textChannel: false,
    attributes: {
      // JT/T 808 0x8103 single-parameter set: id in `index`, raw value in `data`.
      index: parameterId,
      data: hexValue
    }
  })
}

export const startPlayback = async(deviceId, recording) => {
  await sendCommand({
    deviceId,
    type: 'videoPlayback',
    textChannel: false,
    attributes: {
      server: JT1078_SERVER,
      port: JT1078_PLAYBACK_PORT,
      index: recording.channel,
      startTime: new Date(Number(recording.startTime)).toISOString(),
      endTime: new Date(Number(recording.endTime)).toISOString(),
      resourceType: recording.resourceType,
      streamType: recording.streamType,
      storageType: recording.storageType
    }
  })
}

// T/JSATL12 alarm type code -> name, mirroring HuabaoProtocolDecoder's
// ADAS_ALARM_TYPES / DMS_ALARM_TYPES. Used to resolve a numeric adasAlarm /
// dmsAlarm when Traccar didn't send the decoded *AlarmType string (older
// firmware path, or a position that predates that decoder change).
const ADAS_TYPE_BY_CODE = [
  null, 'forwardCollision', 'laneDeparture', 'followingDistance', 'pedestrianCollision',
  'frequentLaneChange', 'roadSignOverrun', 'obstacle'
]
const DMS_TYPE_BY_CODE = [
  null, 'fatigue', 'phoneUse', 'smoking', 'distraction', 'driverAbnormal',
  'handsOffWheel', 'driverChange'
]

// Human labels for those type names.
const SAFETY_EVENT_LABELS = {
  fatigue: 'Fadiga',
  phoneUse: 'Uso de celular',
  smoking: 'Fumando',
  distraction: 'Distração',
  driverAbnormal: 'Motorista ausente',
  handsOffWheel: 'Mãos fora do volante',
  driverChange: 'Troca de motorista',
  forwardCollision: 'Colisão frontal',
  laneDeparture: 'Saída de faixa',
  followingDistance: 'Distância de seguimento',
  pedestrianCollision: 'Colisão com pedestre',
  frequentLaneChange: 'Troca de faixa frequente',
  roadSignOverrun: 'Excesso (placa)',
  obstacle: 'Obstáculo'
}

const resolveSafetyType = (name, code, table) => {
  if (name) return name
  if (code == null) return null
  return table[code] || null
}

// Turn a position carrying an ADAS/DMS alarm into a compact event descriptor.
// Returns null when the position has no active-safety alarm.
export const describeSafetyEvent = (position) => {
  const attributes = position.attributes || {}
  const adas = resolveSafetyType(attributes.adasAlarmType, attributes.adasAlarm, ADAS_TYPE_BY_CODE)
  const dms = resolveSafetyType(attributes.dmsAlarmType, attributes.dmsAlarm, DMS_TYPE_BY_CODE)
  const rawAdas = adas == null && attributes.adasAlarm != null
  const rawDms = dms == null && attributes.dmsAlarm != null
  if (!adas && !dms && !rawAdas && !rawDms) return null
  const kind = (adas || rawAdas) ? 'adas' : 'dms'
  const type = adas || dms || `${kind}${kind === 'adas' ? attributes.adasAlarm : attributes.dmsAlarm}`
  const time = new Date(position.fixTime || position.deviceTime || position.serverTime).getTime()
  return {
    id: `${position.id || time}-${type}`,
    time,
    kind,
    type,
    label: SAFETY_EVENT_LABELS[type] || type,
    level: attributes[`${kind}AlarmLevel`] || null,
    attachments: attributes.alarmAttachments || 0,
    // 32-hex alarm identification number - the key the upload sink files under.
    identifier: attributes.alarmIdentifier || null,
    // JC450: channel 1 = road (ADAS), channel 2 = cabin (DMS).
    channel: kind === 'adas' ? 1 : 2
  }
}

// List the files the camera uploaded for one event. `imei` is the device
// uniqueId; `identifier` is the event's 32-hex alarm id. Returns
// [{ name, bytes, url, channel, label }] with absolute urls, or [] when nothing
// was uploaded. Sink names are <imei>_<identifier>_<channel>_<seq>.<ext>, so the
// channel is read from the name (null if it doesn't follow that pattern).
export const queryEventAttachments = async(imei, identifier) => {
  if (!imei || !identifier) return []
  const response = await fetch(`${MEDIA_ATTACHMENTS_BASE}/${imei}/${identifier}`)
  if (!response.ok) return []
  const body = await response.json()
  return (body.files || []).map(file => {
    const parts = file.name.split('_')
    const channel = parts.length >= 4 ? Number(parts[parts.length - 2]) || null : null
    return {
      name: file.name,
      bytes: file.bytes,
      url: /^https?:\/\//.test(file.url) ? file.url : `${WEBRTC_BASE_URL}${file.url}`,
      video: /\.(mp4|h264)$/i.test(file.name),
      channel,
      label: channel ? `Cam. ${channel} · ${parts[parts.length - 1]}` : parts[parts.length - 1]
    }
  })
}

// Fetch route history for a device/range and extract the ADAS/DMS events.
// (This Traccar instance keeps no events table, so events are read back off the
// position attributes.) Newest first.
const routeEvents = async(deviceId, from, to) => {
  const params = new URLSearchParams({
    deviceId,
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString()
  })
  try {
    const response = await fetch(`/api/reports/route?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      credentials: 'include'
    })
    if (!response.ok) return []
    const positions = await response.json()
    return positions.map(position => describeSafetyEvent(position)).filter(Boolean)
  } catch (error) {
    return []
  }
}

// Events the upload sink already holds media for. Used because this Traccar
// instance can't return historical alarm positions. Filtered to the selected
// [from, to] window (like the route report) and capped to the latest 50.
// Shape mirrors describeSafetyEvent, minus the type (the label doesn't carry it).
const uploadedEvents = async(imei, from, to) => {
  if (!imei) return []
  const start = from ? new Date(from).getTime() : null
  const end = to ? new Date(to).getTime() : null
  try {
    const response = await fetch(`${MEDIA_ATTACHMENTS_BASE}/${imei}`)
    if (!response.ok) return []
    const body = await response.json()
    return (body.events || [])
      .filter(event => event.time)
      .filter(event => (start == null || event.time >= start) && (end == null || event.time <= end))
      .slice(0, 50)
      .map(event => ({
        id: `${event.identifier}-media`,
        time: event.time,
        kind: event.kind || 'dms',
        type: event.type || 'media',
        label: SAFETY_EVENT_LABELS[event.type] || (event.hasVideo ? 'Evento (vídeo)' : 'Evento'),
        level: event.level || null,
        attachments: event.fileCount,
        hasVideo: event.hasVideo,
        identifier: event.identifier,
        channel: event.kind === 'adas' ? 1 : 2
      }))
  } catch (error) {
    return []
  }
}

// Merge the (usually empty) route-report events with the events the sink has
// media for; the typed route-report entry wins when both cover one identifier.
export const queryDeviceEvents = async(deviceId, from, to, imei) => {
  const [routed, uploaded] = await Promise.all([
    routeEvents(deviceId, from, to),
    uploadedEvents(imei, from, to)
  ])
  const byId = new Map()
  uploaded.forEach(event => byId.set(event.identifier, event))
  routed.forEach(event => {
    if (!event.identifier) {
      byId.set(event.id, event)
      return
    }
    const held = byId.get(event.identifier)
    const merged = { ...held, ...event }
    // The alarm identifier carries a nominal attachment count, but on the Jimi
    // fleet it is a firmware constant (always 5) while the camera uploads what
    // it has. Trust the sink's real file count for what the dialog will list.
    if (held && held.attachments != null) {
      merged.attachments = held.attachments
    }
    byId.set(event.identifier, merged)
  })
  return [...byId.values()].sort((first, second) => second.time - first.time)
}

// Build the synthetic "recording" that play()/startPlayback() expect, centred on
// an event with a +/- windowSec seconds margin. `channel` should be a channel the
// camera actually records (event.channel is only a guess from the event kind).
export const eventPlaybackWindow = (event, windowSec = 30, channel = event.channel) => ({
  channel,
  startTime: event.time - windowSec * 1000,
  endTime: event.time + windowSec * 1000,
  resourceType: 2, // video (matches what the JC450 stores)
  streamType: 0,
  storageType: 0
})

const alarmBit = (index) => Math.pow(2, index)

// Catalog of recognisable recording events. `low` / `high` are bitmasks over the
// two halves of the JT/T 1078 resource alarm flag: `low` covers the JT/T 808
// 0x0200 alarm bits, `high` the JT/T 1078 video alarm bits. Several raw bits can
// share one label; each label uses only one half so the 64-bit query value stays
// a safe integer.
export const RECORDING_ALARM_EVENTS = [
  { label: 'SOS', low: alarmBit(0) },
  { label: 'Excesso de velocidade', low: alarmBit(1) + alarmBit(13) },
  { label: 'Fadiga ao volante', low: alarmBit(2) + alarmBit(14) },
  { label: 'Aviso de perigo', low: alarmBit(3) },
  { label: 'Falha do equipamento', low: alarmBit(4) + alarmBit(9) + alarmBit(10) + alarmBit(11) + alarmBit(12) },
  { label: 'Antena GNSS', low: alarmBit(5) + alarmBit(6) },
  { label: 'Tensão baixa', low: alarmBit(7) },
  { label: 'Falha de energia', low: alarmBit(8) },
  { label: 'Tempo de condução excedido', low: alarmBit(18) },
  { label: 'Estacionamento prolongado', low: alarmBit(19) },
  { label: 'Entrada/saída de área', low: alarmBit(20) + alarmBit(21) },
  { label: 'Desvio de rota', low: alarmBit(23) },
  { label: 'Veículo roubado', low: alarmBit(26) },
  { label: 'Ignição não autorizada', low: alarmBit(27) },
  { label: 'Deslocamento não autorizado', low: alarmBit(28) },
  { label: 'Aviso de colisão', low: alarmBit(29) },
  { label: 'Aviso de capotamento', low: alarmBit(30) },
  { label: 'Abertura de porta indevida', low: alarmBit(31) },
  { label: 'Perda de sinal de vídeo', high: alarmBit(0) },
  { label: 'Vídeo obstruído', high: alarmBit(1) },
  { label: 'Falha de armazenamento', high: alarmBit(2) },
  { label: 'Falha do equipamento de vídeo', high: alarmBit(3) },
  { label: 'Excesso de passageiros', high: alarmBit(4) },
  { label: 'Condução anómala', high: alarmBit(5) },
  { label: 'Limite de gravação de alarme atingido', high: alarmBit(6) }
]

const splitAlarmFlag = (alarmFlag) => {
  const value = Number(alarmFlag)
  if (!Number.isFinite(value) || value === 0) return null
  const magnitude = Math.abs(value)
  return {
    low: Math.floor(magnitude % 0x100000000),
    high: Math.floor(magnitude / 0x100000000)
  }
}

// Human-readable event labels for a recording, decoded from the JT/T 1078
// resource-list alarm flag (backend attribute "alarmFlag"). Values above 2^53
// lose precision in JSON, but real-world flags (0 or a few low bits) stay well
// within range.
export const describeRecordingAlarms = (alarmFlag) => {
  const parts = splitAlarmFlag(alarmFlag)
  if (!parts) return []
  const matches = (mask, source) => ((mask || 0) & source) !== 0
  return RECORDING_ALARM_EVENTS
    .filter(event => matches(event.low, parts.low) || matches(event.high, parts.high))
    .map(event => event.label)
}

// 64-bit alarm bitmask to send in the 0x9205 query for a filter selection.
// 'all' / 'any' / unknown -> 0 (no camera-side filter; the client still narrows
// the list). A known label -> that label's mask so the camera pre-filters.
export const recordingAlarmQueryFlag = (selection) => {
  if (!selection || selection === 'all' || selection === 'any') return 0
  const event = RECORDING_ALARM_EVENTS.find(item => item.label === selection)
  return event ? (event.low || 0) + (event.high || 0) * 0x100000000 : 0
}

// Single source of truth for a device's camera channels. Returns channel
// descriptors so callers get stable names now and attribute-driven names later
// (e.g. device.attributes.cameraChannels), without touching the components.
export const getCameraChannels = (device) => {
  const deviceType = Number(device && device.attributes && device.attributes.deviceType)
  let indices
  if (deviceType === 57) {
    indices = [1, 2, 3, 4, 5]
  } else if (deviceType === 60) {
    // iStartek DC600 (4CH)
    indices = [1, 2, 3, 4]
  } else if (deviceType === 56) {
    indices = [1, 2, 3]
  } else {
    indices = [1, 2]
  }
  return indices.map(index => ({
    index,
    name: `Cam. ${index}`
  }))
}

export const hasCameras = (device) => Boolean(
  device && device.attributes && (
    device.attributes.cameraEnabled ||
    [55, 56, 57, 60].includes(Number(device.attributes.deviceType))
  )
)

export const getWhepUrl = (imei, channel = 0) => {
  return `${WEBRTC_BASE_URL}/rtc/live/${channel}/${imei}/whep`
}

export const getPlaybackWhepUrl = (imei, channel = 0) => {
  return `${WEBRTC_BASE_URL}/rtc/playback/${channel}/${imei}/whep`
}

// Per-camera publish status straight from the JT1078 media server, so the
// UI can tell "the camera never connected" (retry the videoStart command)
// from "connected but the stream broke" (retry the WebRTC negotiation).
// Shape: { connected, publishing, decided, hasVideo, hasAudio,
//          videoAgeMs, audioAgeMs, uptimeMs, restarts }
export const getMediaStatus = (imei, channel = 0, namespace = 'live') => {
  return fetch(`${MEDIA_STATUS_BASE}/status/${namespace}/${imei}/${channel}`)
    .then(response => (response.ok ? response.json() : null))
    .catch(() => null)
}
